import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { Accordion, Table } from '@morressier/alembic';
import AccordionTitle from './AccordionTitle';
import {
  Figure,
  FileSet,
  IFileMetaData
} from '@publishing-engine/domain/models';
import { getUpdatedOn } from 'src/utils/getUpdatedOn';
import { DownloadFile } from './Downloadfile';
import { useEffect, useState } from 'react';
import { Tooltip } from '@chakra-ui/tooltip';
import FileViewerButtonText from '../FileViewerButtonText';

const HeadersTitle = ['File Name', 'Updated on'];
const FigureHeadersTitle = ['Figure No.', 'Caption', 'Alt Text', 'Updated on'];
const TableHeadersTitle = ['Table No.', 'Caption', 'Updated on'];
interface TableColumn {
  key: string;
  label: string;
  size: string | number;
  render?: (item?: IFileMetaData | null) => React.ReactNode;
}
interface FigureTableColumn {
  key: string;
  label: string;
  size: string | number;
  render?: (
    item: Figure,
    figureName: string,
    isSubpart?: Boolean
  ) => React.ReactNode;
}

interface FilesLogProps {
  fileset: FileSet | null;
  submissionId: string;
}

const TABLE_COLUMNS = (submissionContextId?: string): TableColumn[] => [
  {
    key: 'File Name',
    label: 'File Name',
    size: 1,
    render: item => (
      <Flex flexDirection="column" alignItems="flex-start" gap={2}>
        <Box maxWidth={'full'} paddingX={2}>
          <Tooltip
            hasArrow
            placement="top"
            label={<Text textStyle="textContext">{item?.name}</Text>}
            borderRadius={4}
            data-cy="info-tooltip"
          >
            <Text
              as="span"
              textStyle="headingItem"
              isTruncated
              textDecoration="none"
              noOfLines={1}
              width={'full'}
              _hover={{
                textDecoration: 'underline',
                textDecorationColor: 'actionPrimaryFill'
              }}
              color="actionPrimaryFill"
            >
              {item && (
                <FileViewerButtonText
                  file={item}
                  submissionContextId={submissionContextId}
                  textStyle="headingItem"
                  wordBreak={'break-word'}
                  whiteSpace="normal"
                  textAlign={'start'}
                >
                  {item.name}
                </FileViewerButtonText>
              )}
            </Text>
          </Tooltip>
        </Box>{' '}
      </Flex>
    )
  },

  {
    key: 'Updated on',
    label: 'Updated on',
    size: 0.5,
    render: item => (
      <Flex alignItems="flex-start" gap={2}>
        <Text textAlign="left" flex={1}>
          {item && getUpdatedOn(item)}
        </Text>
        {item && <DownloadFile file={item} />}
      </Flex>
    )
  }
];
const FIGURE_TABLE_COLUMNS = (): FigureTableColumn[] => [
  {
    key: 'Figure No.',
    label: 'Figure No.',
    size: 1,
    render: (_, figureName, isSubpart) => (
      <>
        <>
          <Flex flexDirection="column" alignItems="flex-start" gap={2}>
            <Box maxWidth={'full'} paddingX={2}>
              {isSubpart ? (
                <Text ml={3} isTruncated textDecoration="none">
                  {figureName}
                </Text>
              ) : (
                <Text textStyle="headingItem"> {figureName}</Text>
              )}
            </Box>
          </Flex>
        </>
      </>
    )
  },
  {
    key: 'Caption',
    label: 'Caption',
    size: 1.5,
    render: item => (
      <Box>
        <Text textAlign="left">{item?.caption}</Text>
      </Box>
    )
  },
  {
    key: 'Alt Text',
    label: 'Alt Text',
    size: 1.5,
    render: item => (
      <Box>
        <Text textAlign="left">{item?.altText}</Text>
      </Box>
    )
  },
  {
    key: 'Updated on',
    label: 'Updated on',
    size: 1,
    render: (item, _, isSubpart) => (
      <Box>
        <>
          <Flex justifyContent={'center'} alignItems={'center'}>
            <Text textAlign="left" flex={1}>
              {item.file ? (
                getUpdatedOn(
                  item?.file,
                  item?.uploadedBy,
                  item?.date,
                  item?.roleName
                )
              ) : (
                <>
                  {isSubpart === false && (
                    <Text as="span" color="gray.500">
                      Composite Figures Provided
                    </Text>
                  )}
                </>
              )}
            </Text>
            {item?.file && <DownloadFile file={item?.file} />}
          </Flex>
        </>
      </Box>
    )
  }
];

const TABLE_TABLE_COLUMNS = (): FigureTableColumn[] => [
  {
    key: 'Table No.',
    label: 'Table No.',
    size: 1,
    render: (_, tableName) => (
      <Flex flexDirection="column" alignItems="flex-start" gap={2}>
        <Box maxWidth={'full'} paddingX={2}>
          <Text textStyle="headingItem">{tableName}</Text>
        </Box>
      </Flex>
    )
  },
  {
    key: 'Caption',
    label: 'Caption',
    size: 1.5,
    render: item => (
      <Box>
        <Text textAlign="left">{item?.caption}</Text>
      </Box>
    )
  },
  {
    key: 'Updated on',
    label: 'Updated on',
    size: 1,
    render: item => (
      <Box>
        <Flex justifyContent={'center'} alignItems={'center'}>
          <Text textAlign="left" flex={1}>
            {getUpdatedOn(
              item?.file,
              item?.uploadedBy,
              item?.date,
              item?.roleName
            )}
          </Text>
          {item?.file && <DownloadFile file={item?.file} />}
        </Flex>
      </Box>
    )
  }
];
const extractAllFiles = (data: FileSet | null): IFileMetaData[] | null => {
  const files: IFileMetaData[] = [];

  // These keys are guaranteed to map to CoverLetter
  const singleKeys: Array<
    keyof Pick<FileSet, 'Manuscript' | 'TitlePage' | 'CoverLetter'>
  > = ['Manuscript', 'TitlePage', 'CoverLetter'];
  if (data === null) {
    return null;
  }
  singleKeys.forEach(key => {
    const file = data[key];
    if (file) files.push(file);
  });

  // Array keys
  const arrayKeys: Array<
    keyof Pick<
      FileSet,
      'Supplementary' | 'AdditionalSupplementary' | 'Figures' | 'Tables'
    >
  > = ['Supplementary', 'AdditionalSupplementary', 'Figures', 'Tables'];

  arrayKeys.forEach(key => {
    const value = data[key];
    if (value) {
      (value as IFileMetaData[]).forEach(f => files.push(f));
    }
  });

  return files;
};

const FilesLog: React.FC<FilesLogProps> = ({ fileset, submissionId }) => {
  if (fileset === null) {
    return null;
  }
  const tableColumns = TABLE_COLUMNS(submissionId);
  const figuretableColumns = FIGURE_TABLE_COLUMNS();
  const tableTableColumns = TABLE_TABLE_COLUMNS();
  const [filesCount, setFilesCount] = useState<number>(0);

  useEffect(() => {
    const allFiles = extractAllFiles(fileset);
    if (allFiles === null) {
      return;
    }
    setFilesCount(allFiles?.length);
  }, []);

  return (
    <>
      <Accordion
        data-testId="integrity-overview"
        chakraAccordionProps={{
          allowMultiple: true,
          defaultIndex: [0],
          style: {
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        accordionItemProps={{
          marginBottom: 1
        }}
        items={[`Files (${filesCount})`].map(title => {
          return {
            title: <AccordionTitle title={title} />,
            content: (
              <VStack align="stretch" spacing={6}>
                <Box
                  borderBottom={'1px'}
                  paddingBottom={2}
                  borderColor="neutralPrimaryBorder"
                  marginTop={3}
                >
                  <Text textStyle="headingItem" mb="2">
                    Manuscript
                  </Text>
                  <Flex width={'100%'} justifyContent={'space-between'}>
                    <Tooltip
                      hasArrow
                      placement="top"
                      label={
                        <Text textStyle="textContext">
                          {fileset?.Manuscript?.name}
                        </Text>
                      }
                      borderRadius={4}
                      data-cy="info-tooltip"
                    >
                      <Text
                        as="span"
                        textStyle="headingItem"
                        isTruncated
                        textDecoration="none"
                        _hover={{
                          textDecoration: 'underline',
                          textDecorationColor: 'actionPrimaryFill'
                        }}
                        color="actionPrimaryFill"
                      >
                        {fileset?.Manuscript && (
                          <FileViewerButtonText
                            file={fileset.Manuscript}
                            submissionContextId={submissionId}
                            textStyle="headingItem"
                            wordBreak={'break-word'}
                            whiteSpace="normal"
                            textAlign={'start'}
                          >
                            {fileset.Manuscript.name}
                          </FileViewerButtonText>
                        )}
                      </Text>
                    </Tooltip>
                    <Flex align="center" gap={2}>
                      <Text textStyle={'textContext'}>
                        {getUpdatedOn(fileset?.Manuscript)}
                      </Text>

                      <DownloadFile file={fileset?.Manuscript} />
                    </Flex>
                  </Flex>
                </Box>
                {fileset?.TitlePage?.name && (
                  <Box
                    borderBottom={'1px'}
                    paddingBottom={2}
                    borderColor="neutralPrimaryBorder"
                  >
                    <Text textStyle="headingItem" mb="2">
                      Title Page
                    </Text>
                    <Flex width={'100%'} justifyContent={'space-between'}>
                      <Tooltip
                        hasArrow
                        placement="top"
                        label={
                          <Text textStyle="textContext">
                            {fileset?.TitlePage?.name}
                          </Text>
                        }
                        borderRadius={4}
                        data-cy="info-tooltip"
                      >
                        <Text
                          as="span"
                          textStyle="headingItem"
                          isTruncated
                          textDecoration="none"
                          _hover={{
                            textDecoration: 'underline',
                            textDecorationColor: 'actionPrimaryFill'
                          }}
                          color="actionPrimaryFill"
                        >
                          {fileset?.TitlePage && (
                            <FileViewerButtonText
                              file={fileset.TitlePage}
                              submissionContextId={submissionId}
                              textStyle="headingItem"
                              wordBreak={'break-word'}
                              whiteSpace="normal"
                              textAlign={'start'}
                            >
                              {fileset.TitlePage.name}
                            </FileViewerButtonText>
                          )}
                        </Text>
                      </Tooltip>
                      <Flex align="center" gap={2}>
                        <Text textStyle={'textContext'}>
                          {getUpdatedOn(fileset?.TitlePage)}
                        </Text>
                        <DownloadFile file={fileset?.TitlePage} />
                      </Flex>
                    </Flex>
                  </Box>
                )}
                {fileset?.CoverLetter?.name && (
                  <Box
                    borderBottom={'1px'}
                    paddingBottom={2}
                    borderColor="neutralPrimaryBorder"
                  >
                    <Text textStyle="headingItem" mb="2">
                      Cover Letter
                    </Text>
                    <Flex width={'100%'} justifyContent={'space-between'}>
                      <Tooltip
                        hasArrow
                        placement="top"
                        label={
                          <Text textStyle="textContext">
                            {fileset?.CoverLetter?.name}
                          </Text>
                        }
                        borderRadius={4}
                        data-cy="info-tooltip"
                      >
                        <Text
                          as="span"
                          textStyle="headingItem"
                          isTruncated
                          textDecoration="none"
                          _hover={{
                            textDecoration: 'underline',
                            textDecorationColor: 'actionPrimaryFill'
                          }}
                          color="actionPrimaryFill"
                        >
                          {fileset?.CoverLetter && (
                            <FileViewerButtonText
                              file={fileset.CoverLetter}
                              submissionContextId={submissionId}
                              textStyle="headingItem"
                              wordBreak={'break-word'}
                              whiteSpace="normal"
                              textAlign={'start'}
                            >
                              {fileset.CoverLetter.name}
                            </FileViewerButtonText>
                          )}
                        </Text>
                      </Tooltip>

                      <Flex align="center" gap={2}>
                        <Text textStyle={'textContext'}>
                          {getUpdatedOn(fileset?.CoverLetter)}
                        </Text>
                        <DownloadFile file={fileset?.CoverLetter} />
                      </Flex>
                    </Flex>
                  </Box>
                )}{' '}
                {fileset?.Supplementary &&
                  fileset?.Supplementary?.length >= 1 && (
                    <Box>
                      <Text textStyle="headingItem" my="3">
                        {`Supplementary File(s) (for review) ${fileset?.Supplementary?.length}`}
                      </Text>
                      <Table>
                        <Table.HeaderRow>
                          {HeadersTitle.map(title => {
                            return (
                              <>
                                <Table.HeaderCell
                                  flex={title === 'File Name' ? '1' : '0.5'}
                                  textAlign="left"
                                >
                                  <Text>{title}</Text>
                                </Table.HeaderCell>
                              </>
                            );
                          })}
                        </Table.HeaderRow>
                        <Table.Body>
                          {fileset?.Supplementary?.map(item => {
                            return (
                              <>
                                <Table.Row>
                                  {tableColumns.map(column => (
                                    <Table.RowCell
                                      key={column.key}
                                      flex={column.size}
                                      textAlign="left"
                                      height={'full'}
                                      borderTop={'none'}
                                    >
                                      {column.render
                                        ? column.render(item)
                                        : null}
                                    </Table.RowCell>
                                  ))}
                                </Table.Row>
                              </>
                            );
                          })}{' '}
                        </Table.Body>
                      </Table>
                    </Box>
                  )}
                {fileset?.AdditionalSupplementary &&
                  fileset?.AdditionalSupplementary?.length >= 1 && (
                    <Box>
                      <Text textStyle="headingItem" my="3">
                        {` Supplementary File(s) (NOT for review) ${fileset?.AdditionalSupplementary.length}`}
                      </Text>
                      <Table>
                        <Table.HeaderRow>
                          {HeadersTitle.map(title => {
                            return (
                              <>
                                <Table.HeaderCell
                                  flex={title === 'File Name' ? '1' : '0.5'}
                                  textAlign="left"
                                >
                                  <Text>{title}</Text>
                                </Table.HeaderCell>
                              </>
                            );
                          })}
                        </Table.HeaderRow>
                        <Table.Body>
                          {fileset?.AdditionalSupplementary?.map(item => {
                            return (
                              <>
                                <Table.Row>
                                  {tableColumns.map(column => (
                                    <Table.RowCell
                                      key={column.key}
                                      flex={column.size}
                                      textAlign="left"
                                      height={'full'}
                                      borderTop={'none'}
                                    >
                                      {column.render
                                        ? column.render(item)
                                        : null}
                                    </Table.RowCell>
                                  ))}
                                </Table.Row>
                              </>
                            );
                          })}{' '}
                        </Table.Body>
                      </Table>
                    </Box>
                  )}
                {fileset?.Figures?.length >= 1 && (
                  <Box>
                    <Text textStyle="headingItem" my="3">
                      {`Figures (${fileset?.Figures?.length})`}
                    </Text>
                    <Table>
                      <Table.HeaderRow>
                        {FigureHeadersTitle.map(title => {
                          return (
                            <>
                              <Table.HeaderCell
                                flex={
                                  title === 'Caption' || title === 'Alt Text'
                                    ? 1.5
                                    : 1
                                }
                                textAlign="left"
                                px={2}
                              >
                                <Text>{title}</Text>
                              </Table.HeaderCell>
                            </>
                          );
                        })}
                      </Table.HeaderRow>
                      <Table.Body>
                        {fileset?.Figures?.map((item, index) => {
                          return (
                            <>
                              <Table.Row>
                                {figuretableColumns.map(column => (
                                  <Table.RowCell
                                    key={column.key}
                                    flex={column.size}
                                    textAlign="left"
                                    height={'full'}
                                    borderTop={'none'}
                                    px={2}
                                  >
                                    {column.render
                                      ? column.render(
                                          item,
                                          `Figure ${index + 1}`,
                                          false
                                        )
                                      : null}
                                  </Table.RowCell>
                                ))}
                              </Table.Row>
                              {item?.isComposite &&
                                item?.subParts?.map((part, subIndex) => {
                                  return (
                                    <Table.Row>
                                      {figuretableColumns.map(column => (
                                        <Table.RowCell
                                          key={column.key}
                                          flex={column.size}
                                          textAlign="left"
                                          height={'full'}
                                          borderTop={'none'}
                                          px={2}
                                        >
                                          {column.render
                                            ? column.render(
                                                part as Figure,
                                                `Figure ${
                                                  index + 1
                                                }${String.fromCharCode(
                                                  97 + subIndex
                                                )}`,
                                                true
                                              )
                                            : null}
                                        </Table.RowCell>
                                      ))}
                                    </Table.Row>
                                  );
                                })}
                            </>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </Box>
                )}{' '}
                {fileset?.Tables?.length >= 1 && (
                  <Box>
                    <Text textStyle="headingItem" my="3">
                      {`Tables (${fileset?.Tables?.length})`}
                    </Text>
                    <Table>
                      <Table.HeaderRow>
                        {TableHeadersTitle.map(title => {
                          return (
                            <>
                              <Table.HeaderCell
                                flex={title === 'Caption' ? 1.5 : 1}
                                textAlign="left"
                                px={2}
                              >
                                <Text>{title}</Text>
                              </Table.HeaderCell>
                            </>
                          );
                        })}
                      </Table.HeaderRow>
                      <Table.Body>
                        {fileset?.Tables?.map((item, index) => {
                          return (
                            <>
                              <Table.Row>
                                {tableTableColumns.map(column => (
                                  <Table.RowCell
                                    key={column.key}
                                    flex={column.size}
                                    textAlign="left"
                                    height={'full'}
                                    borderTop={'none'}
                                    px={2}
                                  >
                                    {column.render
                                      ? column.render(
                                          item,
                                          `Table ${index + 1}`,
                                          false
                                        )
                                      : null}
                                  </Table.RowCell>
                                ))}
                              </Table.Row>
                            </>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </Box>
                )}
              </VStack>
            )
          };
        })}
      />
    </>
  );
};

export default FilesLog;
